import { useState } from 'react';
import { mockInsights } from '../mock';
import { Calendar, Clock, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Insights = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Market Entry', 'DeepTech', 'Healthcare', 'Artificial Intelligence', 'Capital', 'Manufacturing'];

  const filteredInsights = selectedCategory === 'All'
    ? mockInsights
    : mockInsights.filter(insight => insight.category === selectedCategory);

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Insights & Perspectives
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Expert insights on cross-border growth, market entry strategies, and industry trends shaping global business expansion.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-slate-200 px-6 lg:px-8 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-700 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Insights Grid */}
      <section className="py-16 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredInsights.map((insight) => (
              <Card
                key={insight.id}
                className="border-slate-200 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center group-hover:from-blue-50 group-hover:to-slate-50 transition-all duration-300">
                    <div className="text-center p-6">
                      <Badge className="bg-blue-700 text-white mb-3">{insight.category}</Badge>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-blue-700 transition-colors">
                      {insight.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{insight.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(insight.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {insight.readTime}
                      </div>
                    </div>
                    <button className="inline-flex items-center text-blue-700 font-medium hover:text-blue-800 transition-colors">
                      Read more
                      <ChevronRight className="ml-1 w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {filteredInsights.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No insights found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-slate-50 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Stay Updated on Cross-Border Growth Trends
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Subscribe to receive our latest insights and industry perspectives.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
            />
            <button className="bg-blue-700 hover:bg-blue-800 text-white font-medium px-6 py-3 rounded-lg transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
